import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Button } from "./ui/button";
import { useTheme } from "../lib/theme";
import { LOGO_MARK } from "../lib/brand";
import { useAuthSession } from "../lib/authApi";
import { AuthControl, MobileAuthLinks } from "./AuthMenu";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/platform", label: "Platform" },
  { to: "/manufacturing", label: "Manufacturing" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/partners", label: "Partners" },
  { to: "/about", label: "About" },
  { to: "/quote", label: "Instant Quote" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const session = useAuthSession();

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/80 dark:bg-black/60 border-b border-black/5 dark:border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo-link" onClick={() => setOpen(false)}>
          <img src={LOGO_MARK} alt="Vectron Global" className="h-9 sm:h-11 w-auto" />
          <span className="font-heading leading-none">
            <span className="block text-base sm:text-lg font-semibold tracking-wide text-foreground">VECTRON</span>
            <span className="block text-[10px] sm:text-[11px] font-medium tracking-[0.35em] text-primary">GLOBAL</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-medium rounded-full transition-colors duration-200 whitespace-nowrap ${
                  isActive
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
            aria-label="Toggle theme"
            className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-border text-foreground hover:bg-muted transition-colors duration-200"
          >
            {theme === "light" ? <Moon className="h-4 w-4" strokeWidth={1.5} /> : <Sun className="h-4 w-4" strokeWidth={1.5} />}
          </button>
          <AuthControl session={session} />
          <Button asChild className="hidden sm:inline-flex rounded-full px-6" data-testid="header-quote-btn">
            <Link to="/contact">Get a Quote</Link>
          </Button>
          <button
            className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border border-border text-foreground"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl px-4 py-4 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-lg text-sm font-medium ${
                  isActive ? "text-primary bg-primary/10" : "text-foreground hover:bg-muted"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <MobileAuthLinks session={session} onNavigate={() => setOpen(false)} />
          <div className="pt-3">
            <Button asChild className="w-full rounded-full h-11" data-testid="mobile-nav-quote-btn">
              <Link to="/contact" onClick={() => setOpen(false)}>Get a Quote</Link>
            </Button>
          </div>
        </nav>
      )}
    </header>
  );
};
